import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Search, Filter, LogOut, Check } from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    company_name: '',
    industry: '',
    timeline: '',
    status: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }
    fetchQuotes();
  }, [user, navigate]);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.company_name) params.company_name = filters.company_name;
      if (filters.industry) params.industry = filters.industry;
      if (filters.timeline) params.timeline = filters.timeline;
      if (filters.status) params.status = filters.status;

      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes`,
        { params, withCredentials: true }
      );
      setQuotes(data.quotes || []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (quoteId, newStatus) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/quotes/${quoteId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      // Update local state
      setQuotes(quotes.map(q => q.id === quoteId ? { ...q, status: newStatus } : q));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const clearFilters = () => {
    setFilters({ company_name: '', industry: '', timeline: '', status: '' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAFAFA' }}>
        <div style={{ color: '#0D2C22' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA', padding: '40px 20px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl mb-2" style={{ color: '#0D2C22', fontFamily: 'Georgia, serif' }}>
              Sales Leads Pro Dashboard
            </h1>
            <p style={{ color: '#666' }}>Welcome, {user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded transition-colors"
            style={{ background: '#0D2C22', color: '#fff' }}
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={20} style={{ color: '#E5A93D' }} />
            <h2 className="text-xl" style={{ color: '#0D2C22', fontFamily: 'Georgia, serif' }}>Filters</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              type="text"
              placeholder="Search company name"
              value={filters.company_name}
              onChange={(e) => setFilters({ ...filters, company_name: e.target.value })}
              className="px-4 py-2 border rounded"
              style={{ borderColor: '#ddd' }}
            />
            <input
              type="text"
              placeholder="Search industry"
              value={filters.industry}
              onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
              className="px-4 py-2 border rounded"
              style={{ borderColor: '#ddd' }}
            />
            <select
              value={filters.timeline}
              onChange={(e) => setFilters({ ...filters, timeline: e.target.value })}
              className="px-4 py-2 border rounded"
              style={{ borderColor: '#ddd' }}
            >
              <option value="">All Timelines</option>
              <option value="Immediate (1-2 weeks)">Immediate (1-2 weeks)</option>
              <option value="Short-term (1 month)">Short-term (1 month)</option>
              <option value="Medium-term (2-3 months)">Medium-term (2-3 months)</option>
              <option value="Long-term (3+ months)">Long-term (3+ months)</option>
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-4 py-2 border rounded"
              style={{ borderColor: '#ddd' }}
            >
              <option value="">All Statuses</option>
              <option value="New">New</option>
              <option value="Contacted">Contacted</option>
            </select>
          </div>
          
          <div className="flex gap-4 mt-4">
            <button
              onClick={fetchQuotes}
              className="flex items-center gap-2 px-4 py-2 rounded transition-colors"
              style={{ background: '#E5A93D', color: '#fff' }}
            >
              <Search size={18} />
              Apply Filters
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-2 border rounded transition-colors"
              style={{ borderColor: '#ddd', color: '#0D2C22' }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg mb-2" style={{ color: '#666' }}>Total Leads</h3>
            <p className="text-3xl font-bold" style={{ color: '#0D2C22' }}>{quotes.length}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg mb-2" style={{ color: '#666' }}>New</h3>
            <p className="text-3xl font-bold" style={{ color: '#E5A93D' }}>
              {quotes.filter(q => q.status === 'New').length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg mb-2" style={{ color: '#666' }}>Contacted</h3>
            <p className="text-3xl font-bold" style={{ color: '#0D2C22' }}>
              {quotes.filter(q => q.status === 'Contacted').length}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ background: '#0D2C22' }}>
                <tr>
                  <th className="px-4 py-3 text-left text-white">Date</th>
                  <th className="px-4 py-3 text-left text-white">Company</th>
                  <th className="px-4 py-3 text-left text-white">Contact</th>
                  <th className="px-4 py-3 text-left text-white">Industry</th>
                  <th className="px-4 py-3 text-left text-white">Timeline</th>
                  <th className="px-4 py-3 text-left text-white">Status</th>
                  <th className="px-4 py-3 text-left text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {quotes.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center" style={{ color: '#666' }}>
                      No quotes found
                    </td>
                  </tr>
                ) : (
                  quotes.map((quote) => (
                    <tr key={quote.id} className="border-b" style={{ borderColor: '#eee' }}>
                      <td className="px-4 py-4" style={{ color: '#0D2C22' }}>
                        {formatDate(quote.timestamp)}
                      </td>
                      <td className="px-4 py-4">
                        <div style={{ color: '#0D2C22', fontWeight: '600' }}>{quote.company_name}</div>
                        <div style={{ color: '#666', fontSize: '0.875rem' }}>{quote.country}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div style={{ color: '#0D2C22' }}>
                          {quote.contact_first_name} {quote.contact_last_name}
                        </div>
                        <div style={{ color: '#666', fontSize: '0.875rem' }}>{quote.email}</div>
                        <div style={{ color: '#666', fontSize: '0.875rem' }}>{quote.phone}</div>
                      </td>
                      <td className="px-4 py-4" style={{ color: '#0D2C22' }}>
                        {quote.industry}
                      </td>
                      <td className="px-4 py-4" style={{ color: '#0D2C22' }}>
                        {quote.timeline}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className="px-3 py-1 rounded-full text-sm font-medium"
                          style={{
                            background: quote.status === 'New' ? '#FEF3C7' : '#D1FAE5',
                            color: quote.status === 'New' ? '#92400E' : '#065F46'
                          }}
                        >
                          {quote.status}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {quote.status === 'New' ? (
                          <button
                            onClick={() => updateStatus(quote.id, 'Contacted')}
                            className="flex items-center gap-1 px-3 py-1 rounded transition-colors"
                            style={{ background: '#0D2C22', color: '#fff', fontSize: '0.875rem' }}
                          >
                            <Check size={16} />
                            Mark Contacted
                          </button>
                        ) : (
                          <button
                            onClick={() => updateStatus(quote.id, 'New')}
                            className="px-3 py-1 rounded transition-colors"
                            style={{ background: '#E5A93D', color: '#fff', fontSize: '0.875rem' }}
                          >
                            Mark as New
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;